import type {
  SubmissionRecord,
  SubmissionListResponse,
  SubmissionUpdateInput,
  SubmissionMetrics,
  NoteEntry,
  MonthlyReportInfo,
  NwEmailTemplate,
  NwEmailTemplateKind,
  PofLetterMeta,
} from '@qdf/shared';

let baseUrl = '';

// Load API URL from config.json (injected by CDK deployment)
async function getBaseUrl(): Promise<string> {
  if (baseUrl) return baseUrl;
  try {
    const res = await fetch('/config.json');
    const config = await res.json();
    baseUrl = config.apiUrl?.replace(/\/+$/, '') || '';
  } catch {
    baseUrl = '';
  }
  return baseUrl;
}

function getToken(): string {
  return localStorage.getItem('qdf_portal_auth_token') || '';
}

async function portalFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const base = await getBaseUrl();
  const url = `${base}/v1/portal${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...options?.headers,
    },
  });

  if (!res.ok) {
    // If unauthorized, clear the stale token and redirect to login
    if (res.status === 401) {
      localStorage.removeItem('qdf_portal_auth_token');
      window.location.href = '/portal/login';
      throw new Error('Session expired — redirecting to login');
    }
    const errorBody = await res.text();
    throw new Error(`API Error ${res.status}: ${errorBody}`);
  }

  return res.json();
}

export async function fetchSubmissions(params?: {
  type?: string;
  status?: string;
  limit?: number;
  nextToken?: string;
}): Promise<SubmissionListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set('type', params.type);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.nextToken) searchParams.set('nextToken', params.nextToken);
  const qs = searchParams.toString();
  return portalFetch(`/submissions${qs ? `?${qs}` : ''}`);
}

export async function fetchSubmission(id: string): Promise<SubmissionRecord> {
  return portalFetch(`/submissions/${id}`);
}

export async function updateSubmission(
  id: string,
  updates: SubmissionUpdateInput,
): Promise<SubmissionRecord> {
  return portalFetch(`/submissions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function syncSubmission(id: string): Promise<{ message: string }> {
  return portalFetch(`/submissions/${id}/sync`, { method: 'POST' });
}

export async function addNote(id: string, text: string): Promise<NoteEntry> {
  return portalFetch(`/submissions/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export async function fetchMetrics(
  timeRange?: 'today' | 'week' | 'month' | 'all',
  company?: 'leftmain' | 'newwestern',
): Promise<SubmissionMetrics> {
  const params = new URLSearchParams();
  if (timeRange) params.set('timeRange', timeRange);
  if (company) params.set('company', company);
  const qs = params.toString();
  return portalFetch(`/metrics${qs ? `?${qs}` : ''}`);
}

/**
 * New Western two-step flow: explicitly send the decision email after the
 * Approve/Reject decision has been saved. `amended` sends the one-time
 * corrected email after an amended decision.
 */
export async function sendDecisionEmail(
  id: string,
  options?: { amended?: boolean },
): Promise<{ sent: boolean; already_sent?: boolean; recipients?: string[]; message?: string }> {
  return portalFetch(`/submissions/${id}/send-decision-email`, {
    method: 'POST',
    body: JSON.stringify(options?.amended ? { amended: true } : {}),
  });
}

/**
 * New Western third step: send the draw-processed notification after the
 * approval decision email has gone out. Same at-most-once semantics as
 * the decision email.
 */
export async function sendDrawEmail(
  id: string,
): Promise<{ sent: boolean; already_sent?: boolean; recipients?: string[]; message?: string }> {
  return portalFetch(`/submissions/${id}/send-draw-email`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ── New Western Proof of Funds letter (attached to the approval email) ──

/**
 * Upload (or replace) the Proof of Funds PDF for a New Western application.
 * The file travels as base64 JSON; the backend validates it is a real PDF.
 */
export async function uploadPofLetter(
  id: string,
  file: File,
): Promise<{ pof_letter: PofLetterMeta }> {
  const contentBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1)); // strip data:...;base64, prefix
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
  return portalFetch(`/submissions/${id}/pof-letter`, {
    method: 'POST',
    body: JSON.stringify({ file_name: file.name, content_base64: contentBase64 }),
  });
}

/** Metadata + a short-lived download URL for the uploaded POF letter. */
export async function fetchPofLetter(
  id: string,
): Promise<{ pof_letter: PofLetterMeta; download_url: string }> {
  return portalFetch(`/submissions/${id}/pof-letter`);
}

/** Remove a POF letter that has not been emailed yet. */
export async function deletePofLetter(id: string): Promise<{ deleted: boolean }> {
  return portalFetch(`/submissions/${id}/pof-letter`, { method: 'DELETE' });
}

// ── New Western email templates (portal-managed applicant email copy) ──

export async function fetchEmailTemplates(): Promise<{ templates: NwEmailTemplate[] }> {
  return portalFetch('/email-templates');
}

export async function saveEmailTemplate(
  kind: NwEmailTemplateKind,
  template: { subject: string; heading: string; body: string },
): Promise<{ template: NwEmailTemplate }> {
  return portalFetch(`/email-templates/${kind}`, {
    method: 'PUT',
    body: JSON.stringify(template),
  });
}

export async function resetEmailTemplate(
  kind: NwEmailTemplateKind,
): Promise<{ template: NwEmailTemplate }> {
  return portalFetch(`/email-templates/${kind}`, { method: 'DELETE' });
}

export async function fetchReports(): Promise<MonthlyReportInfo[]> {
  return portalFetch('/reports');
}

/** Monthly CSVs are per-company — Left Main and New Western never mix. */
export async function downloadReport(
  yearMonth: string,
  company: 'leftmain' | 'newwestern',
): Promise<{ downloadUrl: string; expiresIn: number }> {
  return portalFetch(`/reports/${yearMonth}?company=${company}`);
}

/**
 * New Western Step 4: sync the saved disbursement details to the accounting
 * mailbox. At most ONE sync per submission (failed sends can be retried).
 */
export async function syncAccounting(
  id: string,
): Promise<{ sent: boolean; already_sent?: boolean; recipients?: string[]; message?: string }> {
  return portalFetch(`/submissions/${id}/sync-accounting`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

